import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    Optional,
    Self,
    computed,
    inject,
    input,
    output,
    signal,
    viewChild
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { FcpInputFieldComponent, InputFieldIconPosition } from '../fcp-input-field';
import { FcpInputTextDirective } from '../fcp-input-text';
import { FcpPopOverComponent } from '../fcp-popover';
import { FcpPeriodPickerService } from './fcp-period-picker.service';

@Component({
    selector: 'fcp-period-picker',
    imports: [
        CommonModule,
        FormsModule,
        DatePickerModule,
        FcpInputFieldComponent,
        FcpInputTextDirective,
        FcpPopOverComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    styleUrl: './fcp-period-picker.component.less',
    template: `
        <div class="fcp-period-picker" [class.fcp-period-picker--fluid]="fluid()">
            @if (inline()) {
                <ng-container [ngTemplateOutlet]="panel" />
            } @else {
                <div
                    class="fcp-period-picker__trigger"
                    [class.fcp-period-picker__trigger--disabled]="isDisabled()"
                    tabindex="0"
                    role="button"
                    #picker
                    [attr.aria-expanded]="isOpen()"
                    [attr.aria-label]="triggerAriaLabel()"
                    (click)="handleTriggerInteraction($event, picker)"
                    (keydown.enter)="handleTriggerInteraction($event, picker)"
                    (keydown.space)="handleTriggerInteraction($event, picker)"
                >
                    <fcp-input-field
                        class="fcp-period-picker__field"
                        iconStyleClass="fc-font-icon fc-font-icon-calendar"
                        [iconPosition]="iconPositionRight"
                        [invalid]="isControlInvalidAndDirty"
                        [floatLabel]="floatLabel()"
                        [label]="label()"
                        [id]="id()"
                    >
                        <ng-template>
                            <input
                                fcpInputText
                                [fluid]="fluid()"
                                readonly
                                [id]="id()"
                                [value]="displayValue()"
                                [placeholder]="floatLabel() ? ' ' : placeholder()"
                                [disabled]="isDisabled()"
                                [class.ng-invalid]="isControlInvalidAndDirty"
                                [class.ng-dirty]="isControlInvalidAndDirty"
                                class="fcp-period-picker__input"
                            />
                        </ng-template>
                    </fcp-input-field>
                </div>

                <fcp-popover
                    #popover
                    [baseZIndex]="1000"
                    [autoZIndex]="true"
                    (onShow)="isOpen.set(true); onShow.emit()"
                    (onHide)="isOpen.set(false)"
                >
                    <ng-container [ngTemplateOutlet]="panel" />
                </fcp-popover>
            }
        </div>

        <ng-template #panel>
            <div class="fcp-period-picker__panel" [class.fcp-period-picker__panel--inline]="inline()">
                <div class="fcp-period-picker__presets">
                    @for (preset of presets(); track preset.label) {
                        <button
                            type="button"
                            class="fcp-period-picker__preset-btn"
                            [class.fcp-period-picker__preset-btn--active]="isActivePreset(preset)"
                            [disabled]="isDisabled()"
                            (click)="applyPreset(preset)"
                        >
                            {{ preset.label }}
                        </button>
                    }
                    <button
                        type="button"
                        class="fcp-period-picker__preset-btn fcp-period-picker__preset-btn--custom"
                        [class.fcp-period-picker__preset-btn--active]="isCustomActive()"
                        [disabled]="isDisabled()"
                        (click)="activateCustomRange()"
                    >
                        {{ customRangeLabel() }}
                    </button>
                </div>

                <!-- Start input + From calendar -->
                <div class="fcp-period-picker__pane fcp-period-picker__pane--start">
                    <div class="fcp-period-picker__date-field">
                        <span class="fcp-period-picker__date-label">{{ startLabel() }}</span>
                        <input
                            fcpInputText
                            size="small"
                            [fluid]="true"
                            class="fcp-period-picker__date-input"
                            [value]="displayFrom()"
                            [placeholder]="datePlaceholder()"
                            [attr.aria-label]="startLabel()"
                            [disabled]="isDisabled()"
                            (input)="onFromDateMaskInput($event)"
                            (blur)="onFromInputCommit($event)"
                            (keydown.enter)="onFromInputCommit($event)"
                        />
                    </div>
                    <p-datepicker
                        [inline]="true"
                        [defaultDate]="firstViewDate()"
                        [minDate]="resolvedMinDate()"
                        [maxDate]="resolvedMaxDate()"
                        [showOtherMonths]="false"
                        (onSelect)="onDateClick($event)"
                        (onMonthChange)="onFirstCalendarMonthChange($event)"
                    >
                        <ng-template pTemplate="date" let-date>
                            <span
                                [class.fcp-period-picker__date--in-range]="isInRange(date)"
                                [class.fcp-period-picker__date--range-start]="isRangeStart(date)"
                                [class.fcp-period-picker__date--range-end]="isRangeEnd(date)"
                                [class.fcp-period-picker__date--single-selected]="isSingleSelected(date)"
                                >{{ date.day }}</span
                            >
                        </ng-template>
                    </p-datepicker>
                </div>

                <!-- End input + To calendar -->
                <div class="fcp-period-picker__pane fcp-period-picker__pane--end">
                    <div class="fcp-period-picker__date-field">
                        <span class="fcp-period-picker__date-label">{{ endLabel() }}</span>
                        <input
                            fcpInputText
                            size="small"
                            [fluid]="true"
                            class="fcp-period-picker__date-input"
                            [value]="displayTo()"
                            [placeholder]="datePlaceholder()"
                            [attr.aria-label]="endLabel()"
                            [disabled]="isDisabled()"
                            (input)="onToDateMaskInput($event)"
                            (blur)="onToInputCommit($event)"
                            (keydown.enter)="onToInputCommit($event)"
                        />
                    </div>
                    <p-datepicker
                        [inline]="true"
                        [defaultDate]="viewDate()"
                        [minDate]="resolvedMinDate()"
                        [maxDate]="resolvedMaxDate()"
                        [showOtherMonths]="false"
                        (onSelect)="onDateClick($event)"
                        (onMonthChange)="onSecondCalendarMonthChange($event)"
                    >
                        <ng-template pTemplate="date" let-date>
                            <span
                                [class.fcp-period-picker__date--in-range]="isInRange(date)"
                                [class.fcp-period-picker__date--range-start]="isRangeStart(date)"
                                [class.fcp-period-picker__date--range-end]="isRangeEnd(date)"
                                [class.fcp-period-picker__date--single-selected]="isSingleSelected(date)"
                                >{{ date.day }}</span
                            >
                        </ng-template>
                    </p-datepicker>
                </div>
            </div>
        </ng-template>
    `
})
export class FcpPeriodPickerComponent implements ControlValueAccessor {
    /* Inputs */
    e2e = input<string | null>(null);
    id = input<string | undefined>(undefined);
    presets = input<DateRangePreset[]>([]);
    disabled = input(false);
    fluid = input(false);
    floatLabel = input<boolean>(false);
    minDate = input<Date | string>();
    maxDate = input<Date | string>();
    locale = input<string | undefined>(undefined);
    placeholder = input<string>('Select date range');
    label = input<string>('');
    customRangeLabel = input<string>('Custom range');
    triggerAriaLabel = input<string>('Select date range');
    startLabel = input<string>('Start');
    endLabel = input<string>('End');
    datePlaceholder = input<string>('DD/MM/YYYY');
    inline = input<boolean>(false);

    /* Outputs */
    onSelect = output<PeriodPickerSelectEvent>();
    onShow = output<void>();

    protected readonly iconPositionRight = InputFieldIconPosition.Right;
    protected readonly fcpPeriodPickerService = inject(FcpPeriodPickerService);

    private readonly popoverComponent = viewChild<FcpPopOverComponent>('popover');

    /* Internal state */
    protected isOpen = signal(false);
    protected fromDate = signal<Date | null>(null);
    protected toDate = signal<Date | null>(null);
    protected activePreset = signal<DateRangePreset | null>(null);

    protected viewDate = signal<Date>(this.fcpPeriodPickerService.currentMonth());
    protected firstViewDate = computed(() => this.fcpPeriodPickerService.prevMonth(this.viewDate()));
    protected selectionState = signal<'empty' | 'first-selected' | 'range-complete'>('empty');
    private _formDisabled = signal<boolean>(false);
    private readonly _customActive = signal<boolean>(false);
    protected readonly isCustomActive = computed(
        () => this._customActive() || (this.activePreset() === null && this.selectionState() !== 'empty')
    );

    protected isDisabled = computed(() => this.disabled() || this._formDisabled());

    protected displayValue = computed(() => {
        const preset = this.activePreset();
        if (preset) return preset.label;
        const from = this.fromDate();
        const to = this.toDate();
        if (!from && !to) return '';
        if (from && to) return `${this.fmt(from)} - ${this.fmt(to)}`;
        return this.fmt(from ?? to!);
    });

    protected displayFrom = computed(() => {
        const d = this.fromDate();
        return d ? this.fmt(d) : '';
    });

    protected displayTo = computed(() => {
        const d = this.toDate();
        return d ? this.fmt(d) : '';
    });

    protected resolvedMinDate = computed(() => this.parseDate(this.minDate()));
    protected resolvedMaxDate = computed(() => this.parseDate(this.maxDate()));

    /* ControlValueAccessor */
    protected onChange: (value: Date[] | null) => void = () => {};
    protected onTouched: () => void = () => {};

    constructor(
        @Optional() @Self() private readonly ngControl: NgControl | null,
        private readonly elementRef: ElementRef<HTMLElement>
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        const target = event.target as Node;
        const overlayEl = this.popoverComponent()?.getOverlayElement();
        if (
            this.isOpen() &&
            document.contains(target) &&
            !this.elementRef.nativeElement.contains(target) &&
            !overlayEl?.contains(target)
        ) {
            if (this.selectionState() === 'first-selected') {
                this.fromDate.set(null);
                this.selectionState.set('empty');
                this._customActive.set(false);
                this.onChange(null);
            }
            queueMicrotask(() => this.onTouched());
        }
    }

    writeValue(value: Date[] | null): void {
        const from = value?.[0] ?? null;
        const to = value?.[1] ?? null;
        this.fromDate.set(from);
        this.toDate.set(to);
        this._customActive.set(false);
        this.syncSelectionState();

        if (from && to) {
            const matched = this.presets().find((p) => {
                const [pFrom, pTo] = p.value;
                return this.isSameDay(from, pFrom) && this.isSameDay(to, pTo);
            });
            this.activePreset.set(matched ?? null);
        } else {
            this.activePreset.set(null);
        }
    }

    private isSameDay(a: Date, b: Date): boolean {
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    registerOnChange(fn: (value: Date[] | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._formDisabled.set(isDisabled);
        if (isDisabled) this.popoverComponent()?.hide();
    }

    hide(): void {
        if (this.isOpen()) this.popoverComponent()?.hide();
    }

    protected onFirstCalendarMonthChange(event: { month: number; year: number }): void {
        // First calendar shows prevMonth(viewDate), so viewDate = event.month + 1
        // new Date handles month overflow (e.g. month=12 → Jan of year+1)
        this.viewDate.set(new Date(event.year, event.month, 1));
    }

    protected onSecondCalendarMonthChange(event: { month: number; year: number }): void {
        // Second calendar IS viewDate directly
        this.viewDate.set(new Date(event.year, event.month - 1, 1));
    }

    protected get isControlInvalidAndDirty(): boolean {
        const control = this.ngControl?.control ?? null;
        return !!control && control.invalid && (control.dirty || control.touched);
    }

    protected handleTriggerInteraction(event: Event, target: HTMLElement): void {
        if (this.isDisabled()) return;
        this.popoverComponent()?.toggle(event, target);
    }

    protected applyPreset(preset: DateRangePreset): void {
        if (this.isDisabled()) return;
        this.activePreset.set(preset);
        this._customActive.set(false);
        const [from, to] = preset.value;
        this.fromDate.set(from);
        this.toDate.set(to);
        this.selectionState.set('range-complete');
        // Second calendar shows toDate's month; first calendar shows the month before it
        this.viewDate.set(this.fcpPeriodPickerService.currentMonth(to));
        this.emitValue(preset);
        queueMicrotask(() => this.onTouched());
    }

    protected activateCustomRange(): void {
        if (this.isDisabled()) return;
        this._customActive.set(true);
        this.activePreset.set(null);
        // Keep panel open for manual calendar selection
    }

    protected isActivePreset(preset: DateRangePreset): boolean {
        return this.activePreset() === preset;
    }

    protected isRangeStart(date: { day: number; month: number; year: number }): boolean {
        if (this.selectionState() !== PeriodPickerSelectionState.RangeComplete) return false;
        const from = this.fromDate();
        if (!from) return false;
        return from.getFullYear() === date.year && from.getMonth() === date.month && from.getDate() === date.day;
    }

    protected isRangeEnd(date: { day: number; month: number; year: number }): boolean {
        if (this.selectionState() !== PeriodPickerSelectionState.RangeComplete) return false;
        const to = this.toDate();
        if (!to) return false;
        return to.getFullYear() === date.year && to.getMonth() === date.month && to.getDate() === date.day;
    }

    protected isSingleSelected(date: { day: number; month: number; year: number }): boolean {
        if (this.selectionState() !== PeriodPickerSelectionState.FirstSelected) return false;
        const from = this.fromDate();
        if (!from) return false;
        return from.getFullYear() === date.year && from.getMonth() === date.month && from.getDate() === date.day;
    }

    protected isInRange(date: { day: number; month: number; year: number }): boolean {
        const from = this.fromDate();
        const to = this.toDate();
        if (!from || !to) return false;
        const d = new Date(date.year, date.month, date.day).getTime();
        const s = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
        const e = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
        const [start, end] = s < e ? [s, e] : [e, s];
        return d > start && d < end;
    }

    protected onFromDateMaskInput(event: Event): void {
        this.applyDateMask(event);
        const input = event.target as HTMLInputElement;
        const parsed = this.parseDdMmYyyy(input.value);
        if (parsed) {
            this.fromDate.set(parsed);
            this.activePreset.set(null);
            this.viewDate.set(this.fcpPeriodPickerService.nextMonth(parsed));
            this.syncSelectionState();
        }
    }

    protected onToDateMaskInput(event: Event): void {
        this.applyDateMask(event);
        const input = event.target as HTMLInputElement;
        const parsed = this.parseDdMmYyyy(input.value);
        if (parsed) {
            this.toDate.set(parsed);
            this.activePreset.set(null);
            this.viewDate.set(this.fcpPeriodPickerService.currentMonth(parsed));
            this.syncSelectionState();
        }
    }

    /** Live masking: auto-inserts "/" after DD and DD/MM as the user types. */
    private applyDateMask(event: Event): void {
        const input = event.target as HTMLInputElement;
        const raw = input.value;
        const cursorPos = input.selectionStart ?? raw.length;

        // Normalise: treat "." as "/", strip everything else that isn't a digit or "/"
        const digits = raw.replace(/[.,]/g, '/').replace(/[^\d]/g, '').slice(0, 8);

        let formatted: string;
        if (digits.length <= 2) {
            formatted = digits;
        } else if (digits.length <= 4) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        } else {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
        }

        // Compute new cursor: count digits before cursor in old value, find same digit
        // position in formatted string so the cursor tracks digits, not slash characters.
        const digitsBeforeCursor = raw.slice(0, cursorPos).replace(/[^\d]/g, '').length;
        let newCursor = formatted.length;
        let seen = 0;
        for (let i = 0; i < formatted.length; i++) {
            if (seen === digitsBeforeCursor) {
                newCursor = i;
                break;
            }
            if (/\d/.test(formatted[i])) seen++;
        }

        input.value = formatted;
        input.setSelectionRange(newCursor, newCursor);
    }

    /** Commit the typed value to the from-date signal on blur / Enter. */
    protected onFromInputCommit(event: Event): void {
        if (this.isDisabled()) return;
        const input = event.target as HTMLInputElement;
        const parsed = this.parseDdMmYyyy(input.value);
        if (parsed) {
            const to = this.toDate();
            if (to && parsed > to) {
                this.fromDate.set(to);
                this.toDate.set(parsed);
            } else {
                this.fromDate.set(parsed);
            }
            this.activePreset.set(null);
            this.syncSelectionState();
            this.emitValue();
            queueMicrotask(() => this.onTouched());
        } else if (!input.value.trim()) {
            this.fromDate.set(null);
            this.toDate.set(null);
            this.activePreset.set(null);
            this.syncSelectionState();
            this.emitValue();
        } else {
            input.value = this.displayFrom();
        }
    }

    /** Commit the typed value to the to-date signal on blur / Enter. */
    protected onToInputCommit(event: Event): void {
        if (this.isDisabled()) return;
        const input = event.target as HTMLInputElement;
        const parsed = this.parseDdMmYyyy(input.value);
        if (parsed) {
            const from = this.fromDate();
            if (!from) {
                this.fromDate.set(parsed);
            } else if (parsed < from) {
                this.toDate.set(from);
                this.fromDate.set(parsed);
            } else {
                this.toDate.set(parsed);
            }
            this.activePreset.set(null);
            this.syncSelectionState();
            this.emitValue();
            queueMicrotask(() => this.onTouched());
        } else if (!input.value.trim()) {
            this.fromDate.set(null);
            this.toDate.set(null);
            this.activePreset.set(null);
            this.syncSelectionState();
            this.emitValue();
        } else {
            input.value = this.displayTo();
        }
    }

    private emitValue(preset?: DateRangePreset): void {
        const from = this.fromDate();
        const to = this.toDate();
        if (from && to) {
            this.onChange([from, to]);
            this.onSelect.emit({ dates: [from, to], preset });
        } else {
            this.onChange(null);
        }
    }

    private fmt(d: Date): string {
        return d.toLocaleDateString(this.locale() ?? 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    /** Parse a DD/MM/YYYY string, validating the resulting date is real. */
    private parseDdMmYyyy(value: string): Date | undefined {
        const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!match) return undefined;
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        const year = parseInt(match[3], 10);
        const d = new Date(year, month, day);
        if (d.getFullYear() === year && d.getMonth() === month && d.getDate() === day) {
            return d;
        }
        return undefined;
    }

    private parseDate(value: Date | string | undefined): Date | undefined {
        if (value == null) return undefined;
        if (value instanceof Date) return value;
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? undefined : d;
    }

    private syncSelectionState(): void {
        const from = this.fromDate();
        const to = this.toDate();
        if (from && to) {
            this.selectionState.set('range-complete');
        } else if (from) {
            this.selectionState.set('first-selected');
        } else {
            this.selectionState.set('empty');
        }
    }

    protected onDateClick(date: Date): void {
        if (this.isDisabled()) return;
        const state = this.selectionState();
        if (state === 'empty') {
            this.fromDate.set(date);
            this.activePreset.set(null);
            this.selectionState.set('first-selected');
        } else if (state === 'first-selected') {
            const from = this.fromDate()!;
            if (date.getTime() < from.getTime()) {
                this.toDate.set(from);
                this.fromDate.set(date);
            } else {
                this.toDate.set(date);
            }
            this.activePreset.set(null);
            this.selectionState.set('range-complete');
            this.emitValue();
            queueMicrotask(() => this.onTouched());
        } else {
            // range-complete: clear and start fresh with this click
            this.toDate.set(null);
            this.fromDate.set(date);
            this.activePreset.set(null);
            this.selectionState.set('first-selected');
            this.onChange(null);
        }
    }
}

export interface DateRangePreset {
    label: string;
    value: [Date, Date];
}

export interface PeriodPickerSelectEvent {
    dates: [Date, Date];
    preset?: DateRangePreset;
}

export enum PeriodPickerSelectionState {
    Empty = 'empty',
    FirstSelected = 'first-selected',
    RangeComplete = 'range-complete'
}
